import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Building2,
    ChevronLeft,
    CreditCard,
    Crown,
    ExternalLink,
    Eye,
    Gift,
    Image,
    LayoutDashboard,
    ShoppingBag
} from 'lucide-react';
import { clubsService, marketplaceService } from '../../../services';
import './ManageClubs.css';
import VenueSubnav from './VenueSubnav';
import VenueAccessBanner from './VenueAccessBanner';
import { getClubPublicPath, getClubStorefrontPath } from '../../../utils/clubPublicPaths';

const VenueStorefront = () => {
    const { clubId } = useParams();
    const navigate = useNavigate();
    const [club, setClub] = useState(null);
    const [subscriptionData, setSubscriptionData] = useState(null);
    const [listings, setListings] = useState([]);
    const [offers, setOffers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const [clubData, venueSubscription, listingData, offerData] = await Promise.all([
                clubsService.getClub(clubId),
                clubsService.getClubSubscription(clubId),
                marketplaceService.getClubListings(clubId, 20).catch(() => []),
                clubsService.getClubOffers(clubId).catch(() => [])
            ]);

            setClub(clubData);
            setSubscriptionData(venueSubscription);
            setListings(Array.isArray(listingData) ? listingData : []);
            setOffers(Array.isArray(offerData) ? offerData : []);
        } catch (loadError) {
            setError(loadError?.message || 'Failed to load storefront manager.');
        } finally {
            setIsLoading(false);
        }
    }, [clubId]);

    useEffect(() => {
        load();
    }, [load]);

    const activeListings = useMemo(
        () => listings.filter((listing) => (listing.status || '').toLowerCase() === 'active'),
        [listings]
    );
    const activeOffers = useMemo(
        () => offers.filter((offer) => offer.isActive !== false),
        [offers]
    );
    const isPremiumVenue = Boolean(subscriptionData?.isPremiumVenue);
    const profileTier = subscriptionData?.profileTier || 'Free';
    const billingStatus = subscriptionData?.subscription?.status || 'Free';
    const publicProfileUrl = getClubPublicPath(club);
    const publicStorefrontUrl = getClubStorefrontPath(club);
    const dashboardStorefrontUrl = `/dashboard/marketplace/venue/${clubId}`;

    if (isLoading) {
        return (
            <div className="manage-clubs-page">
                <div className="manage-clubs-loading">
                    <div className="manage-clubs-spinner"></div>
                    <p>Loading storefront manager...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="manage-clubs-page">
            <button className="club-dashboard-back" onClick={() => navigate(`/dashboard/club/${clubId}/dashboard`)}>
                <ChevronLeft size={18} />
                Back to {club?.name || 'Venue'} Venue Dashboard
            </button>

            <VenueSubnav clubId={clubId} />

            <VenueAccessBanner club={subscriptionData || club} clubId={clubId} navigate={navigate} premiumOnlyFeature featureLabel="Marketplace / shopfront" />

            <div className="manage-section-header">
                <div className="manage-section-header-content">
                    <div className="manage-section-header-icon icon-purple">
                        <ShoppingBag size={24} />
                    </div>
                    <div>
                        <h1>Venue Marketplace / Shopfront</h1>
                        <p>Manage what the public sees for {club?.name}, separate from your day-to-day venue operations.</p>
                    </div>
                </div>
            </div>

            {error && <div className="manage-clubs-error">{error}</div>}

            <div className="dashboard-card-container">
                <div className="dashboard-card">
                    <div className="club-view-card-info" style={{ marginBottom: '14px' }}>
                        <LayoutDashboard size={18} className='club-view-card-info-icon' />
                        <span>
                            <strong>Operational management</strong> is your internal venue setup: events, members, stands, media, till, and communications.
                        </span>
                    </div>
                    <div className="club-view-card-info" style={{ marginBottom: '14px', background: '#f8fafc', borderColor: '#cbd5e1' }}>
                        <Eye size={18} className='club-view-card-info-icon' style={{ color: '#334155' }} />
                        <span>
                            <strong>Storefront presentation</strong> is what the public sees: venue profile copy, branding, perks, and marketplace listings.
                        </span>
                    </div>
                    <div className="club-view-card-info" style={{ background: '#fff7ed', borderColor: '#fdba74' }}>
                        {isPremiumVenue ? <Crown size={18} className='club-view-card-info-icon' style={{ color: '#c2410c' }} /> : <CreditCard size={18} className='club-view-card-info-icon' style={{ color: '#c2410c' }} />}
                        <span>
                            Billing status is <strong>{billingStatus}</strong>, while the public storefront tier is <strong>{profileTier}</strong>.
                            {' '}
                            {isPremiumVenue
                                ? 'Premium storefront features are currently visible to the public.'
                                : 'You can still manage venue content, but the richer premium public presentation is currently hidden.'}
                        </span>
                    </div>
                </div>

                <div className="dashboard-card">
                    <div className="manage-club-members-stats-grid">
                        <div className="manage-club-members-stat">
                            <strong>{profileTier}</strong>
                            <span>Public profile tier</span>
                        </div>
                        <div className="manage-club-members-stat">
                            <strong>{isPremiumVenue ? 'Enabled' : 'Disabled'}</strong>
                            <span>Premium storefront</span>
                        </div>
                        <div className="manage-club-members-stat">
                            <strong>{activeListings.length}</strong>
                            <span>Active listings</span>
                        </div>
                        <div className="manage-club-members-stat">
                            <strong>{activeOffers.length}</strong>
                            <span>Active perks</span>
                        </div>
                    </div>
                </div>

                <div className="dashboard-card">
                    <h2 style={{ marginTop: 0 }}>Manage Public Storefront Content</h2>
                    <p className="club-describer" style={{ marginBottom: '16px' }}>
                        This is the dedicated place for the venue page itself: what copy, branding, listings, and offers appear publicly.
                    </p>
                    {!isPremiumVenue ? (
                        <div className="venue-premium-lock-note" style={{ marginBottom: '16px' }}>
                            The full marketplace / shopfront experience is a Premium venue feature. Free venues can still edit the public profile and perks, but listings, media-led merchandising, and the full storefront journey are unlocked with Premium.
                        </div>
                    ) : null}
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        <button type="button" className="manage-section-add-btn" onClick={() => navigate(`/dashboard/club/${clubId}/edit`)}>
                            <Building2 size={16} />
                            Edit Public Venue Profile
                        </button>
                        <button type="button" className="manage-section-add-btn" onClick={() => navigate(`/dashboard/club/${clubId}/offers`)}>
                            <Gift size={16} />
                            Manage Connect Perks
                        </button>
                        <button
                            type="button"
                            className="manage-section-add-btn"
                            onClick={() => isPremiumVenue ? navigate(`/dashboard/marketplace/my-listings?clubId=${clubId}&sellerMode=venue`) : navigate(`/dashboard/club/${clubId}/plan`)}
                        >
                            <ShoppingBag size={16} />
                            {isPremiumVenue ? 'Manage Venue Listings' : 'Upgrade For Marketplace Listings'}
                        </button>
                        <button
                            type="button"
                            className="manage-section-add-btn"
                            onClick={() => isPremiumVenue ? navigate(`/dashboard/club/${clubId}/media`) : navigate(`/dashboard/club/${clubId}/plan`)}
                        >
                            <Image size={16} />
                            {isPremiumVenue ? 'Manage Venue Media' : 'Upgrade For Venue Media'}
                        </button>
                    </div>
                </div>

                <div className="dashboard-card">
                    <h2 style={{ marginTop: 0 }}>Preview What People See</h2>
                    <p className="club-describer" style={{ marginBottom: '16px' }}>
                        Use these links to check the public venue page and the venue storefront itself.
                    </p>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        <a href={publicProfileUrl} target="_blank" rel="noopener noreferrer" className="manage-section-add-btn" style={{ textDecoration: 'none' }}>
                            <ExternalLink size={16} />
                            Open Public Venue Page
                        </a>
                        {isPremiumVenue ? (
                            <a href={publicStorefrontUrl} target="_blank" rel="noopener noreferrer" className="manage-section-add-btn" style={{ textDecoration: 'none' }}>
                                <ExternalLink size={16} />
                                Open Public Storefront
                            </a>
                        ) : (
                            <button type="button" className="manage-section-add-btn" onClick={() => navigate(`/dashboard/club/${clubId}/plan`)}>
                                <CreditCard size={16} />
                                Upgrade To Unlock Storefront
                            </button>
                        )}
                        <button type="button" className="manage-section-add-btn" onClick={() => isPremiumVenue ? navigate(dashboardStorefrontUrl) : navigate(`/dashboard/club/${clubId}/plan`)}>
                            <Eye size={16} />
                            {isPremiumVenue ? 'Open Dashboard Storefront View' : 'View Upgrade Options'}
                        </button>
                    </div>
                </div>

                <div className="dashboard-card">
                    <h2 style={{ marginTop: 0 }}>Free vs Premium Public Experience</h2>
                    <div className="dashboard-two-column-grid">
                        <div className="club-view-card-info" style={{ marginBottom: 0 }}>
                            <Building2 size={18} className='club-view-card-info-icon' />
                            <span>
                                <strong>Free venue profile</strong>: lighter public presentation, standard listing preview, limited event emphasis, and no prominent public shopfront CTA on the venue page.
                            </span>
                        </div>
                        <div className="club-view-card-info" style={{ marginBottom: 0, background: '#fff7ed', borderColor: '#fdba74' }}>
                            <Crown size={18} className='club-view-card-info-icon' style={{ color: '#c2410c' }} />
                            <span>
                                <strong>Premium venue profile</strong>: richer branding, public shopfront button, stronger storefront presentation, gallery highlights, and more polished visibility for offers and marketplace inventory.
                            </span>
                        </div>
                    </div>
                    <div className="club-view-card-info" style={{ marginTop: '16px', background: '#ecfeff', borderColor: '#67e8f9' }}>
                        <CreditCard size={18} className='club-view-card-info-icon' style={{ color: '#0f766e' }} />
                        <span>
                            Managing the venue and being a premium public venue are intentionally different. Your team can operate the venue regardless, while the premium flag controls what the public-facing experience shows.
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VenueStorefront;
